import { useState, useEffect, useRef } from 'react';
import { LuTriangleAlert } from 'react-icons/lu';
import Drawer from '../Drawer/Drawer';
import { createOrder, fetchMyPartner, sendNotification } from '../../lib/directus';
import { SA_TOWNS } from '../../lib/constants';
import styles from './PlaceOrderDrawer.module.css';


function SearchableDropdown({ options, value, onChange, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const filtered = query
    ? options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(opt) { onChange(opt); setQuery(opt); setOpen(false); }
  function handleChange(e) { setQuery(e.target.value); onChange(''); setOpen(true); }

  return (
    <div className={styles.searchDropdown} ref={ref}>
      <input
        className={styles.searchDropdownInput}
        value={query}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && (
        <div className={styles.searchDropdownList}>
          {filtered.length === 0
            ? <div className={styles.searchDropdownEmpty}>No towns found</div>
            : filtered.map(opt => (
              <div key={opt} className={styles.searchDropdownOption} onMouseDown={() => handleSelect(opt)}>
                {opt}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

export default function PlaceOrderDrawer({ item, onClose, onSubmit }) {
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!item) return null;

  const stockValue  = (item.unitPrice || 0) * (item.rawQuantity || 0);
  const transport   = 1000;
  const platformFee = parseFloat((stockValue * 0.01).toFixed(2));
  const total       = (stockValue + transport + platformFee).toFixed(2);

  async function handleSubmit() {
    setLoading(true);
    setError('');
    try {
      const partner = await fetchMyPartner();
      const order = await createOrder({
        batch:             item._id,
        platform_fee:      platformFee,
        delivery_location: deliveryLocation,
        escrow_status:     'pending_approval',
        ...(partner ? { buyer: partner.id } : {}),
      });
      if (item.sellerUserId) {
        sendNotification(
          item.sellerUserId,
          'New order received',
          `A new order has been placed for your batch of ${item.commodity}${item.grade ? ' (' + item.grade + ')' : ''} — ${item.quantity}. Total: ${item.totalAmount}. Please review and approve the order.`
        ).catch(console.error);
      }
      onSubmit();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const footer = (
    <button className={styles.submitBtn} onClick={handleSubmit} disabled={loading}>
      {loading ? 'Submitting...' : 'Submit order for approval'}
    </button>
  );

  return (
    <Drawer
      isOpen={!!item}
      title={`${item.commodity} • ${item.grade}`}
      onClose={onClose}
      footer={footer}
    >
      {/* Order info */}
      <div className={styles.card}>
        <div className={styles.threeCol}>
          <div>
            <p className={styles.fieldLabel}>Quantity</p>
            <p className={styles.fieldValue}>{item.quantity}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Warehouse</p>
            <p className={styles.fieldValue}>{item.location}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Seller</p>
            <p className={styles.fieldValue}>{item.seller}</p>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <p className={styles.fieldLabel}>Amount</p>
          <p className={styles.amountValue}>R{stockValue.toFixed(2)}</p>
        </div>
      </div>

      {/* Delivery location */}
      <div className={styles.card}>
        <p className={styles.cardTitle}>Delivery location</p>
        <SearchableDropdown
          options={SA_TOWNS}
          value={deliveryLocation}
          onChange={setDeliveryLocation}
          placeholder="Search town..."
        />
      </div>

      {/* Order summary */}
      <div className={styles.card}>
        <p className={styles.cardTitle}>Order summary</p>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Stock value</span>
          <span className={styles.summaryVal}>R{stockValue.toFixed(0)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Estimated transport</span>
          <span className={styles.summaryVal}>R{transport}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Platform Fee (1%)</span>
          <span className={styles.summaryVal}>R{platformFee.toFixed(0)}</span>
        </div>
        <div className={styles.divider} />
        <div className={styles.summaryRow}>
          <span className={styles.totalLabel}>Total estimated</span>
          <span className={styles.totalVal}>R{total}</span>
        </div>
      </div>

      {/* Finance approval banner */}
      <div className={styles.financeBanner}>
        <LuTriangleAlert size={16} color="#c27803" style={{ flexShrink: 0 }} />
        <div>
          <p className={styles.bannerTitle}>Finance Approval Required</p>
          <p className={styles.bannerText}>After submission, your Finance Officer must approve and lock funds in escrow.</p>
        </div>
      </div>

      {error && (
        <p style={{ color: 'var(--red-color)', fontSize: 13, padding: '0 4px' }}>{error}</p>
      )}
    </Drawer>
  );
}
